import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {Box, IconButton} from '@mui/material';
import {DatePicker as MUIDatePicker} from '@mui/x-date-pickers/DatePicker';
import {DateRangePicker} from '@mui/x-date-pickers-pro/DateRangePicker';
import {MultiInputDateRangeField} from '@mui/x-date-pickers-pro/MultiInputDateRangeField';
import {DateTime} from 'luxon';
import {equals} from 'ol/extent';
import {type FC, useCallback, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useAvailableDates} from '@src/hooks/useAvailableDates';
import useDateTimeParams from '@src/hooks/useDateTimeParams';
import useVisualizationParams from '@src/hooks/useVisualizationParams';
import {RootState} from '@src/store';
import {isSingleDayDateRange} from '@src/utils/dates';

type MapExtent = RootState['map']['extent'];

const mapExtentsEqual = (
  extent1: MapExtent | null,
  extent2: MapExtent | null,
) => {
  if (extent1 === null || extent2 === null) {
    return false;
  }
  // note equals performs equality check for floating point numbers
  return equals(extent1, extent2);
};

const DatePicker: FC = () => {
  const {layerId} = useVisualizationParams();
  const {parsedDateTimeFrom, parsedDateTimeTo, setDateTimeParams} =
    useDateTimeParams();

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [shouldFetchAvailableDates, setShouldFetchAvailableDates] =
    useState(false);
  const shouldTriggerDateChange = useRef(false);

  const {availableDates, setSearchRange, isLoading} = useAvailableDates(
    {
      from: parsedDateTimeTo?.startOf('month') ?? null,
      to: parsedDateTimeTo?.endOf('month') ?? null,
    },
    {
      enabled: shouldFetchAvailableDates,
    },
  );

  const mapExtent = useSelector((state: RootState) => state.map.extent);
  const prevMapExtentRef = useRef<MapExtent | null>(mapExtent);
  const searchDirectionRef = useRef<'prev' | 'next' | null>(null);

  const monthsSearchedRef = useRef<number>(1);
  const MAX_MONTHS_TO_SEARCH = 24;

  const isSingleDate = isSingleDayDateRange(
    parsedDateTimeFrom,
    parsedDateTimeTo,
  );

  const minDate = DateTime.fromISO('2000-01-01').startOf('day');
  const maxDate = DateTime.now().endOf('day');

  const getValidDateTimeFrom = useCallback(
    (dateTime?: DateTime): DateTime => {
      if (!dateTime) {
        return parsedDateTimeFrom!;
      }
      if (dateTime < minDate) {
        return minDate.startOf('day');
      } else if (isSingleDate && dateTime > maxDate) {
        return maxDate.startOf('day');
      } else if (!isSingleDate && dateTime > parsedDateTimeTo!) {
        return parsedDateTimeTo!.minus({days: 1}).startOf('day');
      }
      return dateTime;
    },
    [isSingleDate, maxDate, minDate, parsedDateTimeFrom, parsedDateTimeTo],
  );

  const getValidDateTimeTo = useCallback(
    (dateTime?: DateTime): DateTime => {
      if (!dateTime) {
        return parsedDateTimeTo!;
      }
      if (dateTime > maxDate) {
        return maxDate.endOf('day');
      } else if (isSingleDate && dateTime < minDate) {
        return minDate.endOf('day');
      } else if (!isSingleDate && dateTime < parsedDateTimeFrom!) {
        return parsedDateTimeFrom!.plus({days: 1}).endOf('day');
      }
      return dateTime;
    },
    [isSingleDate, maxDate, minDate, parsedDateTimeFrom, parsedDateTimeTo],
  );

  const setValidDateTimes = useCallback(
    ({
      dateTimeFrom,
      dateTimeTo,
    }: {
      dateTimeFrom?: DateTime;
      dateTimeTo?: DateTime;
    }) => {
      setDateTimeParams({
        dateTimeFrom: getValidDateTimeFrom(dateTimeFrom).toISO(),
        dateTimeTo: getValidDateTimeTo(dateTimeTo).toISO(),
      });
    },
    [getValidDateTimeFrom, getValidDateTimeTo, setDateTimeParams],
  );

  const setSingleDayDateTime = useCallback(
    (newDateTime: DateTime) => {
      if (!newDateTime.isValid) {
        return;
      }
      shouldTriggerDateChange.current = false;
      setValidDateTimes({
        dateTimeFrom: newDateTime.startOf('day'),
        dateTimeTo: newDateTime.endOf('day'),
      });
    },
    [setValidDateTimes],
  );

  const handleDateRangeChange = (
    newDateTimes: [DateTime | null, DateTime | null],
  ) => {
    const [newDateTimeFrom, newDateTimeTo] = newDateTimes;
    if (!newDateTimeFrom?.isValid || !newDateTimeTo?.isValid) {
      return;
    }
    if (newDateTimeFrom && newDateTimeTo) {
      setValidDateTimes({
        dateTimeFrom: newDateTimeFrom.startOf('day'),
        dateTimeTo: newDateTimeTo.endOf('day'),
      });
    }
  };

  const handleMonthChange = (isRange?: boolean) => (dateTime: DateTime) => {
    setSearchRange({
      from: dateTime.startOf('month'),
      to: isRange
        ? dateTime.plus({months: 1}).endOf('month')
        : dateTime.endOf('month'),
    });
    setShouldFetchAvailableDates(true);
  };

  const searchCurrentMonth = useCallback(() => {
    if (!parsedDateTimeTo) {
      return;
    }
    setShouldFetchAvailableDates(true);
    shouldTriggerDateChange.current = true;
    setSearchRange({
      from: parsedDateTimeTo.startOf('month'),
      to: parsedDateTimeTo.endOf('month'),
    });
  }, [parsedDateTimeTo, setSearchRange]);

  const searchPreviousMonth = useCallback(
    (monthsBack: number = 1) => {
      if (!parsedDateTimeTo || monthsBack > MAX_MONTHS_TO_SEARCH) {
        return;
      }
      const prevMonth = parsedDateTimeTo
        .minus({months: monthsBack})
        .endOf('month');
      if (prevMonth < minDate) {
        return;
      }
      setShouldFetchAvailableDates(true);
      shouldTriggerDateChange.current = true;
      setSearchRange({
        from: prevMonth.startOf('month'),
        to: prevMonth.endOf('month'),
      });
    },
    [parsedDateTimeTo, minDate, setSearchRange],
  );

  const searchNextMonth = useCallback(
    (monthsForward: number = 1) => {
      if (!parsedDateTimeTo || monthsForward > MAX_MONTHS_TO_SEARCH) {
        return;
      }
      const nextMonth = parsedDateTimeTo
        .plus({months: monthsForward})
        .startOf('month');
      if (nextMonth > maxDate) {
        return;
      }
      setShouldFetchAvailableDates(true);
      shouldTriggerDateChange.current = true;
      setSearchRange({
        from: nextMonth.startOf('month'),
        to: nextMonth.endOf('month'),
      });
    },
    [parsedDateTimeTo, maxDate, setSearchRange],
  );

  const handlePrev = () => {
    if (!parsedDateTimeFrom) {
      return;
    }
    searchDirectionRef.current = 'prev';
    monthsSearchedRef.current = 1;

    if (!mapExtentsEqual(mapExtent, prevMapExtentRef.current)) {
      searchCurrentMonth();
      return;
    }

    const previousDates = availableDates
      .filter(date => date < parsedDateTimeFrom)
      .sort((a, b) => b.toMillis() - a.toMillis());

    if (previousDates.length > 0) {
      setSingleDayDateTime(previousDates[0]);
    } else {
      searchPreviousMonth();
    }
  };

  const handleNext = () => {
    if (!parsedDateTimeTo) {
      return;
    }
    searchDirectionRef.current = 'next';
    monthsSearchedRef.current = 1;

    if (!mapExtentsEqual(mapExtent, prevMapExtentRef.current)) {
      searchCurrentMonth();
      return;
    }

    const nextDates = availableDates
      .filter(date => date > parsedDateTimeTo)
      .sort((a, b) => b.toMillis() - a.toMillis());

    if (nextDates.length > 0) {
      setSingleDayDateTime(nextDates[nextDates.length - 1]);
    } else {
      searchNextMonth();
    }
  };

  useEffect(() => {
    if (shouldFetchAvailableDates) {
      setShouldFetchAvailableDates(false);
    }
  }, [availableDates, shouldFetchAvailableDates]);

  useEffect(() => {
    if (
      !availableDates ||
      !parsedDateTimeTo ||
      !shouldTriggerDateChange.current ||
      isLoading
    ) {
      return;
    }

    const sortedDates = availableDates.sort(
      (a, b) => b.toMillis() - a.toMillis(),
    );

    if (mapExtentsEqual(mapExtent, prevMapExtentRef.current)) {
      if (sortedDates.length > 0) {
        if (searchDirectionRef.current === 'prev') {
          setSingleDayDateTime(sortedDates[0]);
        } else if (searchDirectionRef.current === 'next') {
          setSingleDayDateTime(sortedDates[sortedDates.length - 1]);
        }
      } else {
        if (searchDirectionRef.current === 'prev') {
          searchPreviousMonth(++monthsSearchedRef.current);
        } else if (searchDirectionRef.current === 'next') {
          searchNextMonth(++monthsSearchedRef.current);
        }
      }
      return;
    }

    // map extent has changed from previous search
    if (searchDirectionRef.current === 'prev') {
      const previousDates = sortedDates.filter(
        date => date.endOf('day') < parsedDateTimeTo,
      );
      if (previousDates.length > 0) {
        setSingleDayDateTime(previousDates[0]);
      } else {
        searchPreviousMonth();
      }
    } else if (searchDirectionRef.current === 'next') {
      const nextDates = sortedDates.filter(
        date => date.startOf('day') > parsedDateTimeTo,
      );
      if (nextDates.length > 0) {
        setSingleDayDateTime(nextDates[nextDates.length - 1]);
      } else {
        searchNextMonth();
      }
    }
    prevMapExtentRef.current = mapExtent;
  }, [
    availableDates,
    parsedDateTimeTo,
    setSingleDayDateTime,
    mapExtent,
    searchPreviousMonth,
    searchNextMonth,
    isLoading,
  ]);

  if (!parsedDateTimeFrom || !parsedDateTimeTo) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
      }}
    >
      {isSingleDate ? (
        <>
          <IconButton
            onClick={handlePrev}
            aria-label="Previous available date"
            title="Previous available date"
            sx={{color: 'primary.main'}}
            disabled={parsedDateTimeTo <= minDate || !layerId}
          >
            <KeyboardArrowLeftIcon />
          </IconButton>
          <MUIDatePicker
            format="yyyy-LL-dd"
            timezone="UTC"
            label="Date"
            open={openDatePicker}
            onClose={() => setOpenDatePicker(false)}
            slotProps={{
              textField: {
                error: false,
                size: 'small',
                onClick: () => {
                  setShouldFetchAvailableDates(true);
                  setOpenDatePicker(true);
                },
                onBlur: () =>
                  setValidDateTimes({
                    dateTimeFrom: parsedDateTimeFrom,
                    dateTimeTo: parsedDateTimeTo,
                  }),
              },
              openPickerButton: {
                sx: {
                  display: 'none',
                },
              },
              day: ownerState => {
                const isAvailable = availableDates?.some(availableDate =>
                  availableDate.hasSame(ownerState.day, 'day'),
                );

                return {
                  sx: {
                    ...(!isAvailable && {
                      color: 'text.disabled',
                      opacity: 0.5,
                    }),
                  },
                };
              },
            }}
            value={parsedDateTimeTo}
            onChange={newDateTime =>
              setSingleDayDateTime(newDateTime as DateTime)
            }
            onMonthChange={handleMonthChange()}
            minDate={minDate}
            maxDate={maxDate}
          />

          <IconButton
            onClick={handleNext}
            aria-label="Next available date"
            title="Next available date"
            sx={{color: 'primary.main'}}
            disabled={parsedDateTimeTo >= maxDate || !layerId}
          >
            <KeyboardArrowRightIcon />
          </IconButton>
        </>
      ) : (
        <DateRangePicker
          format="yyyy-LL-dd"
          timezone="UTC"
          slots={{
            field: MultiInputDateRangeField,
          }}
          slotProps={{
            // @ts-expect-error type issue with MUI
            textField: ({position}) => ({
              error: false,
              size: 'small',
              label:
                position === 'start' ? 'Start date (UTC)' : 'End date (UTC)',
              onBlur: () =>
                setValidDateTimes({
                  dateTimeFrom:
                    position === 'start' ? parsedDateTimeFrom : undefined,
                  dateTimeTo: position === 'end' ? parsedDateTimeTo : undefined,
                }),
            }),
            field: {
              // @ts-expect-error type issue with MUI
              sx: {
                width: '100%',
              },
            },
            day: ownerState => {
              const isAvailable = availableDates?.some(availableDate =>
                availableDate.hasSame(ownerState.day, 'day'),
              );

              return {
                sx: {
                  ...(!isAvailable && {
                    color: 'text.disabled',
                    opacity: 0.5,
                  }),
                },
              };
            },
          }}
          onOpen={() => handleMonthChange(true)(parsedDateTimeFrom)}
          value={[parsedDateTimeFrom, parsedDateTimeTo]}
          onMonthChange={handleMonthChange(true)}
          disableAutoMonthSwitching
          onChange={handleDateRangeChange}
          minDate={minDate}
          maxDate={maxDate}
        />
      )}
    </Box>
  );
};

export default DatePicker;
